import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import { IconArrowExport } from '@nxsys/components/AppIconSvg';
import AppCard from '@nxsys/components/AppCard';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';

import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { companyActions } from '@nxsys/store/slices/company/reducer/companySlice';
import { GetCompanyListParams } from '@nxsys/store/slices/company/types';

import { ICompany } from '@nxsys/types/models/company';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getColumns } from './columns';
import { csvConfigExport } from '@nxsys/utils/export';
import { ExportToCsv } from 'export-to-csv';
import { showMessage } from '@nxsys/utils/message';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import CreateCompanyModal from '../create';
import { useUpdateCompanyStatus } from '@nxsys/hooks/api/company';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';

const CompanyListing = () => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const { companyList } = useSelector((state: AppState) => state.companyStore);
  const { isBureauEmployer, isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const { list, pagination, loading, queryParams } = companyList;
  const [selectedRows, setSelectedRows] = useState<ICompany[]>([]);
  const navigate = useNavigateWithQuery();
  const [isOpenCreateCompanyModal, setIsOpenCreateCompanyModal] =
    useState(false);

  const { run: updateCompanyStatus, loading: isUpdatingCompanyStatus } =
    useUpdateCompanyStatus();

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const handleFetchData = (params: GetCompanyListParams) => {
    const newParams = {
      ...params,
      paymentModalEmployee: isBureauEmployer
        ? PAYMENT_MODEL_ENUM.PAYEE
        : undefined,
    };

    dispatch(companyActions.getCompanyList(newParams));
  };

  const refetch = () =>
    handleFetchData({
      ...queryParams,
    });

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFetchData({
        ...queryParams,
        sortField: sorter.field ?? '',
        sortType: sorter.order,
      });
    } else {
      handleFetchData({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const handleSearch = useCallback(
    debounce(
      (e: React.ChangeEvent<HTMLInputElement>) =>
        handleFetchData({ ...queryParams, search: e.target.value }),
      500,
    ),
    [queryParams],
  );

  const companyLabel = isBureau
    ? messages['common.bureau.companies']
    : messages['sidebar.app.companies'];

  const renderTitle = useCallback(() => {
    return (
      <AppPageTitle prefix={companyLabel} separator="" title="" />
    );
  }, [companyLabel]);

  const renderRightContent = () => {
    return (
      <div className="flex items-center gap-x-4">
        <AppButton
          variant="outlined"
          type="primary"
          name={messages['common.export']}
          onClick={handleExportSelected}
          leftIcon={<IconArrowExport />}
        />
        <AppButton
          type="primary"
          name={messages['common.addNew']}
          onClick={() => setIsOpenCreateCompanyModal(true)}
          leftIcon={<PlusOutlined />}
        />
      </div>
    );
  };

  const handleExport = (data: ICompany[]) => {
    const dataExport = data.map((item) => ({
      id: item?.id || '',
      companyName: item?.companyName || '',
      companyNo: item?.companyNo || '',
      email: item?.email || '',
      telephone: item?.telephone || '',
      VATRegistered: item?.VATRegistered || '',
      UTRNo: item?.UTRNo || '',
      cisStatus: item?.cisStatus || '',
      cisVerificationNo: item?.cisVerificationNo || '',
      cisVerificationDate: item?.cisVerificationDate || '',
      NICategory: item?.NICategory || '',
      paymentFrequency: item?.paymentFrequency || '',
    }));

    const headers = [
      isBureau ? messages['common.employerId'] : messages['common.companyId'],
      isBureau
        ? messages['common.bureau.companyName']
        : messages['common.companyName'],
      isBureau
        ? messages['common.bureau.companyNo']
        : messages['common.companyNo'],
      messages['common.emailID'],
      messages['common.phone'],
      messages['common.vatRegistered'],
      isIreland ? 'TRN Number' : messages['common.utrNumber'],
      messages['common.cisStatus'],
      messages['common.cisVerificationNumber'],
      messages['common.cisVerificationDate'],
      messages['common.niCategory'],
      messages['common.payFrequency'],
    ];

    const exportName = csvConfigExport(companyLabel, headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  const handleViewCompany = (company: ICompany) => {
    navigate(`/companies/${company.id}`);
  };

  const handleToggleCompany = ({
    company,
    checked,
  }: {
    company: ICompany;
    checked: boolean;
  }) => {
    AppConfirmation.open({
      title: checked
        ? messages['company.activateTitle'] ?? 'Active Company'
        : messages['company.deactivateTitle'] ?? 'Deactive Company',
      paragraph: `Are you sure you want to ${
        checked ? 'active' : 'deactive'
      } "${company.companyName ?? ''}"?`,
      type: 'warning',
      icon: 'IconWarning',
      okText: messages['common.confirm'],
      onConfirm: async () => {
        const response = await updateCompanyStatus(company.id, {
          isActive: checked,
        });
        if (response.success || (response as any).isSuccess) {
          showMessage({
            type: 'success',
            content: messages['common.companyStatusUpdated'],
          });

          dispatch(
            companyActions.getCompanyList({
              ...queryParams,
              page: 0,
              size: 20,
            }),
          );
        } else {
          showMessage({
            type: 'error',
            content: response.message,
          });
        }
      },
    });
  };

  const isFiltering = !!queryParams?.search;

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={renderRightContent()}
    >
      <AppCard>
        <AppsHeader>
          <AppInputSearch onChange={handleSearch} />
        </AppsHeader>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading || isUpdatingCompanyStatus}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1500, y: 700 }}
            columns={getColumns({
              messages,
              handleViewCompany,
              handleToggleCompany,
              isBureau,
              isIreland,
            })}
            onChangePagination={onChangePagination}
            rowSelection={{
              type: 'checkbox',
              onChange: (_: React.Key[], selectedRows: any[]) => {
                setSelectedRows(selectedRows);
              },
            }}
            onChangeTable={onChangeTable}
            noDataType={isFiltering ? 'noDataFound' : 'noDataCreated'}
            addNewBtnOnClick={() => setIsOpenCreateCompanyModal(true)}
            dataTypeName={companyLabel}
          />
        </AppsContent>
      </AppCard>
      <CreateCompanyModal
        isOpen={isOpenCreateCompanyModal}
        toggle={() => setIsOpenCreateCompanyModal(false)}
        refetchList={refetch}
      />
    </AppsContainer>
  );
};

export default CompanyListing;
